import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Empty from 'components/common/Empty';
import ExpandablePanel from 'components/common/ExpandablePanel';
import RadioGroup from 'components/common/fields/radio_group';
import Search from '@material-ui/icons/Search';
import SearchBy from 'components/common/SearchBy';
import Table from 'components/common/table';
import { all, isEmpty, isNil, propSatisfies, prepend } from 'ramda';
import { ROWS_PER_PAGE } from 'consts';
import { useSelector, useDispatch } from 'react-redux';
import { decryptPayerSSN } from 'sdk/payer';
import { getAuth } from 'util/index';
import { load } from 'model/payer';
import { load as loadFspConfigPayALoan } from 'model/fspConfigPayALoan';
import { update as updatePageSetting } from 'model/pageSetting';
import { selector as pageSettingSelector } from 'selectors/pageSetting';
import { useHistory } from 'react-router-dom';
import { formatSearchBy, initialize } from 'components/common/searchByUtils';
import {
  LOAD as GUEST_LOAN_SEARCH_LOAD,
  load as guestLoanSearchLoad,
} from 'model/guestLoanSearch';
import { selectedFspIdSelector } from 'selectors/fsp';
import selector from 'selectors/payerSearch';
import { guestLoanSelector } from 'selectors/guestLoanSearch';
import { makeStyles } from '@material-ui/core/styles';
import { Grid } from '@mui/material';
import { isUpdatingState } from 'reducers/updating';
import {
  businessColumnMeta,
  defaultPayerType,
  payerTypeMeta,
  personColumnMeta,
} from './config/payers';
import { guestColumnMeta } from './config/guestPayers';
import PayersWelcomeMessage from './PayersWelcomeMessage';
import { formatPayerTypeSearchBy, getSearchFields } from './consts';
import { getLocationFromPayer } from '../paths';
import GuestPayers from './GuestPayers';
import useGuestPayerEnabled from '../../../../hooks/useGuestPayerEnabled';

const useStyles = makeStyles((theme) => ({
  empty: {
    position: 'relative',
    minHeight: 290,
  },
  hide: {
    display: 'none',
  },
  radioGroup: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(8),
  },
  text: {
    textAlign: 'center',
  },
  welcome: {
    width: '55%',
    margin: '12vh auto',
    textAlign: 'center',
  },
}));

// Utility to build guest loan search request body from search fields
/**
 * @param {SearchFieldType[]} searchBys
 * @returns {Object} requestBody for guest loan search
 */
function buildGuestLoanSearchRequestBody(searchBys) {
  const requestBody = { ownerType: 'guest' };
  searchBys.forEach(({ field, value }) => {
    if (field === 'loanNumber' && value && value.trim() !== '') {
      requestBody.loanNumber = { value: value.trim() };
    }
    if (field === 'allEmail' && value && value.trim() !== '') {
      requestBody.email = { value: value.trim() };
    }
  });
  // Remove keys with undefined or empty values
  Object.keys(requestBody).forEach((key) => {
    const v = requestBody[key];
    if (
      v == null ||
      (typeof v === 'object' &&
        Object.values(v).every((val) => val == null || val === ''))
    ) {
      delete requestBody[key];
    }
  });
  return requestBody;
}

const isGuestOwner = (payer) =>
  typeof payer?.ownerType === 'string' &&
  payer.ownerType.toLowerCase() === 'guest';

const Payers = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(getAuth);
  const payers = useSelector(selector);
  const fspId = useSelector(selectedFspIdSelector);
  const guestPayerEnabled = useGuestPayerEnabled();
  const settingType = 'payer';

  // Get saved page settings for payer type persistence
  const pageSettings = useSelector((state) =>
    pageSettingSelector(state, 0, settingType)
  );

  useEffect(() => {
    if (fspId) {
      dispatch(loadFspConfigPayALoan(fspId));
    }
  }, [dispatch, fspId]);

  const [landing, setLanding] = useState(payers.length === 0);
  // Initialize with a placeholder; will be set by useEffect
  const [payerType, setPayerType] = useState(defaultPayerType);
  // Initialize pagination state from pageSettings to persist across navigation
  const [page, setPage] = useState(pageSettings.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    pageSettings.rowsPerPage || ROWS_PER_PAGE[0]
  );
  const [currentSearchBys, setCurrentSearchBys] = useState(
    pageSettings.searchBys || []
  );
  const [guestSearchLoading, setGuestSearchLoading] = useState(false);
  const guestSearchStartedRef = useRef(false);

  // Restore pagination state from pageSettings when they become available (e.g., after navigation)
  // Note: We intentionally exclude local state from dependencies to prevent infinite loops.
  // This effect only runs when Redux pageSettings change, not when local state changes.
  useEffect(() => {
    if (pageSettings.page !== undefined && pageSettings.page !== page) {
      setPage(pageSettings.page);
    }
    if (
      pageSettings.rowsPerPage !== undefined &&
      pageSettings.rowsPerPage !== rowsPerPage
    ) {
      setRowsPerPage(pageSettings.rowsPerPage);
    }
    if (
      pageSettings.searchBys &&
      pageSettings.searchBys.length > 0 &&
      currentSearchBys.length === 0
    ) {
      setCurrentSearchBys(pageSettings.searchBys);
      // If we have saved search params and payers, show results instead of landing
      if (payers.length > 0) {
        setLanding(false);
      }
    }
  }, [
    currentSearchBys.length,
    page,
    pageSettings.page,
    pageSettings.rowsPerPage,
    pageSettings.searchBys,
    payers.length,
    rowsPerPage,
  ]);

  const filteredPayerTypeMeta = useMemo(
    () =>
      payerTypeMeta.filter(
        (type) => type.value !== 'guest' || guestPayerEnabled
      ),
    [guestPayerEnabled]
  );

  // Validate payerType whenever guestPayerEnabled changes
  useEffect(() => {
    if (payerType === 'guest' && !guestPayerEnabled) {
      setPayerType(defaultPayerType);
    }
  }, [guestPayerEnabled, payerType]);

  // Restore payerType from saved page settings when they become available
  useEffect(() => {
    const savedPayerType = pageSettings.payerType;
    if (savedPayerType) {
      if (savedPayerType === 'guest' && !guestPayerEnabled) {
        setPayerType(defaultPayerType);
      } else {
        setPayerType(savedPayerType);
      }
    }
  }, [pageSettings.payerType, guestPayerEnabled]);

  const searchFields = useMemo(() => getSearchFields(payerType), [payerType]);
  const columnMeta = useMemo(() => {
    if (payerType === 'person')
      return personColumnMeta(fspId, decryptPayerSSN, auth) || [];
    if (payerType === 'guest')
      return guestColumnMeta(fspId, decryptPayerSSN, auth) || [];
    return businessColumnMeta(fspId, decryptPayerSSN, auth) || [];
  }, [payerType, fspId, auth]);
  const safeColumnMeta = Array.isArray(columnMeta) ? columnMeta : [];

  const isLoading = false;
  const guestLoans = useSelector(guestLoanSelector);
  const guestSearchUpdating = useSelector((state) =>
    isUpdatingState(state, GUEST_LOAN_SEARCH_LOAD.ACTION)
  );

  useEffect(() => {
    setLanding(payers.length === 0);
  }, [payers]);

  useEffect(() => {
    if (guestSearchUpdating) {
      guestSearchStartedRef.current = true;
      return;
    }

    if (guestSearchLoading && guestSearchStartedRef.current) {
      setGuestSearchLoading(false);
      guestSearchStartedRef.current = false;
    }
  }, [guestSearchLoading, guestSearchUpdating]);

  const handleRowClick = useCallback(
    (payer) => {
      if (payer && payer.zfspId && payer.id) {
        history.push(getLocationFromPayer(payer));
      }
    },
    [history]
  );

  const handleTogglePayerType = useCallback(
    (newPayerType) => {
      setPayerType(newPayerType);
      setPage(0);
      const searchFields = getSearchFields(newPayerType);
      const { updatedValue } = initialize(4, searchFields, []);
      const searchBys = prepend(
        formatPayerTypeSearchBy(newPayerType),
        updatedValue
      );
      dispatch(
        updatePageSetting({
          page: 0,
          payerId: 0,
          payerType: newPayerType,
          searchBys,
          type: 'payer',
        })
      );
    },
    [dispatch]
  );

  const handleSearch = useCallback(
    (searchBys /** : SearchFieldType[] */, offset = 0, limit = 100) => {
      setLanding(false);
      setCurrentSearchBys(searchBys);
      // Persist search parameters to Redux for state preservation across navigation
      dispatch(
        updatePageSetting({
          payerId: 0,
          searchBys,
          type: 'payer',
        })
      );
      if (payerType === 'guest') {
        const requestBody = buildGuestLoanSearchRequestBody(searchBys);
        const loanNumberValue =
          requestBody.loanNumber && requestBody.loanNumber.value;
        const emailValue = requestBody.email && requestBody.email.value;

        if (loanNumberValue) {
          setGuestSearchLoading(true);
          let action = guestLoanSearchLoad(
            { fspId, loanNumber: loanNumberValue },
            auth
          );
          if (!action.auth) action = { ...action, auth };
          dispatch(action);
        } else if (emailValue) {
          setGuestSearchLoading(true);
          let action = guestLoanSearchLoad(
            { fspId, email: emailValue, limit: 5, offset: 0 },
            auth
          );
          if (!action.auth) action = { ...action, auth };
          dispatch(action);
        }
      } else {
        const requestLimit = Math.min(limit + 1, 100);
        const searchParams = { fspId, limit: requestLimit, offset };
        searchBys.forEach(({ field, operator, value }) => {
          if (value !== undefined && value !== null && value !== '') {
            const formatted = formatSearchBy(field, operator, value);
            Object.assign(searchParams, formatted);
          }
        });
        dispatch(load(searchParams));
      }
    },
    [auth, dispatch, fspId, payerType]
  );

  const handleSearchButtonPressed = useCallback(
    (args /** : SearchFieldType[] */) => {
      const empty = all(
        propSatisfies(
          (val) =>
            isNil(val) ||
            isEmpty(val) ||
            (typeof val === 'string' && val.trim() === ''),
          'value'
        ),
        args
      );
      setLanding(empty);
      setPage(0);
      // Persist page reset to Redux
      dispatch(
        updatePageSetting({
          page: 0,
          payerId: 0,
          type: 'payer',
        })
      );
      if (!empty) {
        const searchBys = [...args];
        if (!searchBys.some(({ field }) => field === 'type')) {
          searchBys.push(formatPayerTypeSearchBy(payerType));
        }
        handleSearch(searchBys);
      }
    },
    [dispatch, handleSearch, payerType]
  );

  const handleTableChange = useCallback(
    (event, { page: newPage, rowsPerPage: newRowsPerPage }) => {
      const numericRowsPerPage = Number(newRowsPerPage) || rowsPerPage;
      const pageChanged = newPage !== page;
      const rowsPerPageChanged = numericRowsPerPage !== rowsPerPage;

      setPage(newPage);
      setRowsPerPage(numericRowsPerPage);

      const effectivePage = rowsPerPageChanged ? 0 : newPage;
      dispatch(
        updatePageSetting({
          page: effectivePage,
          payerId: 0,
          rowsPerPage: numericRowsPerPage,
          type: 'payer',
        })
      );

      if (currentSearchBys.length > 0 && (pageChanged || rowsPerPageChanged)) {
        const offset = rowsPerPageChanged ? 0 : newPage * numericRowsPerPage;
        handleSearch(currentSearchBys, offset, numericRowsPerPage);
        if (rowsPerPageChanged) {
          setPage(0);
        }
      }
    },
    [currentSearchBys, dispatch, handleSearch, page, rowsPerPage]
  );

  const nonGuestPayers = useMemo(
    () => payers.filter((payer) => !isGuestOwner(payer)),
    [payers]
  );

  return (
    <ExpandablePanel
      expandable={false}
      fullHeight
      heightOffset={53}
      icon={Search}
      title="Payer Search"
    >
      <div>
        <Grid container justifyContent="center">
          <Grid item>
            <RadioGroup
              classes={{ root: classes.radioGroup }}
              label="Payer Type"
              onChange={handleTogglePayerType}
              radios={filteredPayerTypeMeta}
              value={payerType}
            />
          </Grid>
          <Grid item>
            <SearchBy
              expanded
              fields={searchFields}
              onSearch={handleSearchButtonPressed}
              priorityIndex={4}
              settingType={settingType}
            />
          </Grid>
        </Grid>

        <PayersWelcomeMessage in={landing} />
        {!landing && (
          <>
            {payerType === 'guest' && guestPayerEnabled && (
              <GuestPayers
                data={guestLoans}
                columnMeta={guestColumnMeta()}
                isLoading={guestSearchLoading}
              />
            )}
            {payerType !== 'guest' && nonGuestPayers.length > 0 && (
              <Table
                columnMeta={safeColumnMeta}
                data={nonGuestPayers}
                idMeta="id"
                isLoading={isLoading}
                onChange={handleTableChange}
                onRowClick={handleRowClick}
                order={null}
                orderBy={null}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE}
                serverResultCount={payers.length}
                serverPaginated
              />
            )}
            {payerType !== 'guest' && nonGuestPayers.length === 0 && (
              <div className={classes.empty}>
                <Empty
                  subheading="Try modifying the current search parameters"
                  title="No Payers Found"
                />
              </div>
            )}
          </>
        )}
      </div>
    </ExpandablePanel>
  );
};

Payers.propTypes = {};
Payers.defaultProps = {};

export default Payers;
